import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import AdminLoginPage from "./AdminLoginPage";
import GlobalThresholdPanel from "../components/admin/GlobalThresholdPanel";
import LotOverridePanel from "../components/admin/LotOverridePanel";
import NotificationPanel from "../components/admin/NotificationPanel";
import OpsActionPanel from "../components/admin/OpsActionPanel";
import { AdminButton, Notice } from "../components/admin/AdminPrimitives";
import { clearSession, loadSession, setUnauthorizedHandler } from "../lib/adminApi";
import { useAdminSettings } from "../hooks/useAdmin";
import type { AdminSession } from "../types/admin";

/** 로그인 후 본문 — 설정을 한 번 불러와 각 패널에 나눠준다. */
function AdminConsole({
  session,
  onLogout,
}: {
  session: AdminSession;
  onLogout: () => void;
}) {
  const { data, isLoading, error } = useAdminSettings(session.token);

  return (
    <div className="min-h-full bg-slate-50 text-slate-900 dark:bg-surface-dark dark:text-slate-100">
      <AppHeader
        showAdminLink={false}
        right={
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {session.username}
            </span>
            <AdminButton onClick={onLogout}>로그아웃</AdminButton>
          </div>
        }
      />

      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-12">
        <div className="mb-6 flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold">관리자</h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              변경 사항은 저장 즉시 판정에 반영됩니다. 재배포·재기동이 필요 없습니다.
            </p>
          </div>
          <Link
            to="/"
            className="inline-flex min-h-[2.75rem] items-center text-xs text-slate-500 underline-offset-2 hover:underline sm:min-h-0 dark:text-slate-400"
          >
            상태 페이지 →
          </Link>
        </div>

        {error ? (
          <Notice tone="error">{error.message}</Notice>
        ) : isLoading || !data ? (
          <div className="py-16 text-center text-sm text-slate-400 dark:text-slate-500">
            설정을 불러오는 중…
          </div>
        ) : (
          <div className="space-y-6">
            {/* 요청의 핵심 기능이라 가장 위에 둔다. */}
            <LotOverridePanel
              token={session.token}
              globalThresholds={data.thresholds}
            />
            <GlobalThresholdPanel
              token={session.token}
              current={data.thresholds}
              defaults={data.defaults}
            />
            <NotificationPanel
              token={session.token}
              notifications={data.notifications}
            />
            <OpsActionPanel token={session.token} />
          </div>
        )}

        <footer className="mt-10 border-t border-surface-border pt-6 text-xs text-slate-500 dark:border-surface-darkBorder dark:text-slate-400">
          vestellalab · bridgeChecker 관리자
        </footer>
      </main>
    </div>
  );
}

/**
 * `/admin` — 로그인 게이트 + 설정 콘솔.
 *
 * 세션은 localStorage 에 두고, 어느 요청에서든 401 이 나면 즉시 로그인 화면으로 되돌린다
 * (토큰 만료/서버 시크릿 교체). 만료 시각이 지난 토큰은 요청을 보내기 전에 걸러낸다.
 */
export default function AdminPage() {
  const [session, setSession] = useState<AdminSession | null>(() => loadSession());

  useEffect(() => {
    setUnauthorizedHandler(() => setSession(null));
    return () => setUnauthorizedHandler(null);
  }, []);

  if (!session) {
    return <AdminLoginPage onSuccess={setSession} />;
  }

  return (
    <AdminConsole
      session={session}
      onLogout={() => {
        clearSession();
        setSession(null);
      }}
    />
  );
}
